'use client';

import * as React from 'react';
import { DayPicker, DayProps } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import {
    CalendarApartmentResponseDto,
    CalendarEventType,
    DocumentType,
} from '@/features/calendar/types/calendar-dto';
import { isWithinInterval, startOfDay, subDays } from 'date-fns';

const CalendarEventConfig = (type: CalendarEventType) => {
    switch (type) {
        case CalendarEventType.Reservation:
            return {
                eventColor: 'bg-warning-1000',
            };
        case CalendarEventType.Accommodation:
            return {
                eventColor: 'bg-info-1000',
            };
        case CalendarEventType.HouseUse:
            return {
                eventColor: 'bg-success-1000',
            };
    }
};

type CalendarProps = {
    apartments?: CalendarApartmentResponseDto[];
    showReservationDetails?: (
        id: string,
        type: DocumentType,
        apartmentId?: string,
        checkInDate?: Date,
        checkOutDate?: Date,
    ) => void;
} & React.ComponentProps<typeof DayPicker>;

function BookingCalendar({
    className,
    classNames,
    showOutsideDays = false,
    apartments = [],
    showReservationDetails,
    ...props
}: CalendarProps) {
    const renderDay = ({ date }: DayProps) => {
        if (!date) return null;

        const apartmentEvents = apartments.flatMap(
            (apartment) =>
                apartment.documents
                    ?.filter((doc) =>
                        isWithinInterval(date, {
                            start: startOfDay(doc.checkInDate),
                            end: startOfDay(subDays(doc.checkOutDate, 1)),
                        }),
                    )
                    .map((doc) => ({
                        ...doc,
                        apartmentId: apartment.apartmentId,
                    })) || [],
        );

        return (
            <div className="relative size-9 flex items-center justify-center">
                <span>{date.getDate()}</span>
                {apartmentEvents.map((doc, index) => (
                    <>
                        <div
                            key={index}
                            className={cn(
                                'absolute w-full h-0.5 bottom-0',
                                CalendarEventConfig(doc.eventType)?.eventColor,
                            )}
                        />
                        <div
                            className="absolute inset-0 cursor-pointer"
                            onClick={() =>
                                showReservationDetails?.(
                                    doc.documentId,
                                    doc.documentType,
                                    doc.apartmentId,
                                    doc.checkInDate,
                                    doc.checkOutDate,
                                )
                            }
                        />
                    </>
                ))}
            </div>
        );
    };

    return (
        <DayPicker
            disableNavigation
            fixedWeeks
            weekStartsOn={1}
            showOutsideDays={showOutsideDays}
            className={cn(className)}
            classNames={{
                month: 'space-y-4 select-none',
                caption: 'flex justify-center',
                caption_label: 'text-sm font-medium text-popover-1000',
                head_row: 'flex',
                head_cell: 'w-9 text-xs font-normal',
                row: 'flex w-full',
                cell: 'size-9 p-0 text-center relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
                day: cn(
                    buttonVariants({ variant: 'ghost' }),
                    'size-9 p-0 text-xs font-normal aria-selected:opacity-100 cursor-default',
                ),
                day_today: 'bg-popover-100',
                day_outside: 'opacity-50',
                day_disabled: 'opacity-50',
                day_hidden: 'invisible',
                ...classNames,
            }}
            components={{ DayContent: renderDay }}
            {...props}
        />
    );
}

BookingCalendar.displayName = 'Calendar';

export { BookingCalendar };
